"use client"

import type { ReactNode } from "react"

import { useTranslations } from "next-intl"

import { CategoryFacet } from "@/components/marketplace/listing/category-facet"
import { facetBodyFor } from "@/components/marketplace/listing/facet-body"
import { facetsWithSelections, toggledFacetValues } from "@/components/marketplace/listing/facet-options"
import { PriceFacet } from "@/components/marketplace/listing/price-facet"
import type { FacetOption } from "@/components/marketplace/listing/types"
import { CountBadge, Stack, Text, Toggle } from "@/components/ui"
import { withAttribute } from "@/lib/types/search"
import type { SearchFacet, SearchQuery } from "@/lib/types/search"

interface FilterPanelProps {
  query: SearchQuery
  categoryOptions?: FacetOption[]
  facets?: SearchFacet[]
  onUpdateQuery: (updates: Partial<SearchQuery>) => void
  labelPlacement?: "inside" | "outside"
}

const CARD = "bg-surface rounded-12x border-line-faint shadow-panel border p-500"

const SectionTitle = ({ children, count }: { children: ReactNode; count?: number }) => (
  <Text as="h3" family="text" className="text-detail text-fg-heading flex items-center gap-200 font-medium">
    {children}
    {count !== undefined && count > 0 && <CountBadge count={count} tone="brand" />}
  </Text>
)

interface FlatSectionProps {
  title?: string
  count?: number
  placement: "inside" | "outside"
  children: ReactNode
}

const FlatSection = ({ title, count, placement, children }: FlatSectionProps) =>
  placement === "outside" && title ? (
    <Stack gap="200" as="section">
      <SectionTitle count={count}>{title}</SectionTitle>
      <Stack gap="350" className={CARD}>
        {children}
      </Stack>
    </Stack>
  ) : (
    <Stack gap="350" as="section" className={CARD}>
      {title && <SectionTitle count={count}>{title}</SectionTitle>}
      {children}
    </Stack>
  )

export const FilterPanel = ({ query, categoryOptions, facets, onUpdateQuery, labelPlacement = "inside" }: FilterPanelProps) => {
  const t = useTranslations("search")
  const resolvedFacets = facetsWithSelections(facets, query.attributes)

  return (
    <Stack gap="250">
      {categoryOptions && categoryOptions.length > 0 && (
        <FlatSection title={t("panel.category")} placement={labelPlacement}>
          <CategoryFacet options={categoryOptions} selected={query.categories} onChange={(categories) => onUpdateQuery({ categories })} />
        </FlatSection>
      )}
      <FlatSection title={t("panel.priceTotal")} placement={labelPlacement}>
        <PriceFacet min={query.priceMin} max={query.priceMax} onChange={(min, max) => onUpdateQuery({ priceMin: min, priceMax: max })} />
      </FlatSection>
      <FlatSection placement={labelPlacement}>
        <div className="flex items-center justify-between gap-300">
          <Text as="span" family="text" className="text-detail text-fg-heading min-w-0 font-medium">
            {t("panel.inStockOnly")}
          </Text>
          <Toggle checked={query.inStockOnly !== false} ariaLabel={t("panel.inStockOnly")} onChange={(checked) => onUpdateQuery({ inStockOnly: checked })} />
        </div>
      </FlatSection>
      {resolvedFacets.map((facet) => {
        if (facet.values.length === 0) return null
        const selected = query.attributes[facet.key] ?? []
        const Body = facetBodyFor(facet.key)
        return (
          <FlatSection key={facet.key} title={facet.label} count={selected.length} placement={labelPlacement}>
            <Body
              options={facet.values}
              selected={selected}
              onToggle={(value) => onUpdateQuery({ attributes: withAttribute(query.attributes, facet.key, toggledFacetValues(selected, value)) })}
            />
          </FlatSection>
        )
      })}
    </Stack>
  )
}
